/**
 * JobApplicantsDrawer — right-side slide-out that lists every candidate who
 * applied to a specific requisition. Each row exposes a "View passport"
 * clickable button that opens the applicant's Talent Passport in a new tab
 * for detail review. Master-admin gate is at the endpoint layer.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
    X, Loader2, FileText, ShieldCheck, IdCard, MapPin, Sparkles, ExternalLink,
    Mail, Phone, History, ChevronDown, ChevronUp, Paperclip, StickyNote, Trash2, Send, MessageCircle,
} from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { toast } from "@/components/ui/sonner";
import { ApplicantChatPanel } from "@/components/messaging/ApplicantChatPanel";
import api, { formatApiError, backendUrl } from "@/lib/api";

const STATUS_TONE = {
    new: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30",
    reviewing: "bg-sky-500/10 text-sky-700 border-sky-500/30",
    shortlisted: "bg-violet-500/10 text-violet-700 border-violet-500/30",
    rejected: "bg-rose-500/10 text-rose-700 border-rose-500/30",
    hired: "bg-amber-500/10 text-amber-700 border-amber-500/30",
};

const completionTone = (pct) => {
    if (pct >= 70) return "text-emerald-600 bg-emerald-500/10 border-emerald-500/30";
    if (pct >= 40) return "text-sky-600 bg-sky-500/10 border-sky-500/30";
    if (pct > 0) return "text-amber-600 bg-amber-500/10 border-amber-500/30";
    return "text-slate-500 bg-slate-500/10 border-slate-500/30";
};

const relativeTime = (iso) => {
    if (!iso) return "";
    try {
        const then = new Date(iso).getTime();
        const diff = Math.max(0, Date.now() - then);
        const mins = Math.round(diff / 60_000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.round(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.round(hrs / 24);
        if (days < 30) return `${days}d ago`;
        return new Date(iso).toLocaleDateString();
    } catch {
        return "";
    }
};

const formatActor = (evt) => {
    if (!evt) return "";
    if (evt.by_kind === "system" || evt.by === "applicant") return "the applicant";
    return evt.by || "an admin";
};

export const JobApplicantsDrawer = ({ job, open, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [statusFilter, setStatusFilter] = useState("any");
    const [savingId, setSavingId] = useState(null);
    const [expandedHistoryId, setExpandedHistoryId] = useState(null);
    const [selected, setSelected] = useState(() => new Set());
    const [bulkStatus, setBulkStatus] = useState("reviewing");
    const [bulkNote, setBulkNote] = useState("");
    const [bulkSaving, setBulkSaving] = useState(false);
    const [noteDrafts, setNoteDrafts] = useState({});
    const [noteSavingId, setNoteSavingId] = useState(null);

    const load = useCallback(async () => {
        if (!job?.id) return;
        setLoading(true);
        try {
            const { data: d } = await api.get(`/admin/jobs/${job.id}/applicants`);
            setData(d);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, [job?.id]);

    useEffect(() => { if (open) load(); }, [open, load]);
    useEffect(() => {
        if (open) {
            setStatusFilter("any");
            setExpandedHistoryId(null);
            setSelected(new Set());
            setBulkStatus("reviewing");
            setBulkNote("");
            setNoteDrafts({});
        }
    }, [open, job?.id]);

    const toggleSelect = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const selectAllFiltered = (visible) => {
        setSelected((prev) => {
            const next = new Set(prev);
            const allPresent = visible.every((a) => next.has(a.id));
            if (allPresent) visible.forEach((a) => next.delete(a.id));
            else visible.forEach((a) => next.add(a.id));
            return next;
        });
    };

    const runBulkUpdate = async () => {
        const ids = [...selected];
        if (ids.length === 0 || !job?.id) return;
        setBulkSaving(true);
        try {
            const { data: resp } = await api.patch(
                `/admin/jobs/${job.id}/applicants/bulk-status`,
                { applicant_ids: ids, status: bulkStatus, note: bulkNote.trim() },
            );
            const undoSnapshot = resp.undo || [];
            const targetStatus = bulkStatus;
            // Sonner toast with a 30-second Undo action. Clicking Undo
            // reverses every row in the snapshot back to its prev status
            // AND pulls the exact event we just appended.
            toast.success(
                `Bulk update — updated ${resp.updated}${resp.unchanged ? `, ${resp.unchanged} already at ${targetStatus}` : ""}${resp.failed?.length ? `, ${resp.failed.length} failed` : ""}.`,
                {
                    duration: 30_000,
                    action: undoSnapshot.length > 0 ? {
                        label: "Undo",
                        onClick: async () => {
                            try {
                                await api.patch(
                                    `/admin/jobs/${job.id}/applicants/bulk-status/undo`,
                                    { undo: undoSnapshot },
                                );
                                toast.success(`Reverted ${undoSnapshot.length} applicant${undoSnapshot.length === 1 ? "" : "s"}.`);
                                await load();
                            } catch (undoErr) {
                                toast.error(formatApiError(undoErr));
                            }
                        },
                    } : undefined,
                    // Distinct testid on the Undo button so the harness can key on it.
                    // Sonner renders the action button natively; we tag via classname
                    // for reliability across versions.
                    className: "iter111-bulk-undo",
                },
            );
            setSelected(new Set());
            setBulkNote("");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setBulkSaving(false);
        }
    };

    const addNote = async (applicantId) => {
        const text = (noteDrafts[applicantId] || "").trim();
        if (!text) { toast.error("Note is empty."); return; }
        setNoteSavingId(applicantId);
        try {
            const { data: note } = await api.post(`/admin/applications/${applicantId}/notes`, { note: text });
            setData((d) => d && {
                ...d,
                applicants: d.applicants.map((x) =>
                    x.id === applicantId
                        ? { ...x, notes: [...(x.notes || []), note] }
                        : x,
                ),
            });
            setNoteDrafts((prev) => ({ ...prev, [applicantId]: "" }));
            toast.success("Note added.");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setNoteSavingId(null);
        }
    };

    const deleteNote = async (applicantId, noteId) => {
        try {
            await api.delete(`/admin/applications/${applicantId}/notes/${noteId}`);
            setData((d) => d && {
                ...d,
                applicants: d.applicants.map((x) =>
                    x.id === applicantId
                        ? { ...x, notes: (x.notes || []).filter((n) => n.id !== noteId) }
                        : x,
                ),
            });
            toast.success("Note deleted.");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const updateStatus = async (a, next) => {
        if (!job?.id || a.status === next) return;
        setSavingId(a.id);
        // Optimistic — flip UI immediately, revert on failure.
        const prevStatus = a.status;
        setData((d) => d && { ...d, applicants: d.applicants.map((x) => x.id === a.id ? { ...x, status: next } : x) });
        try {
            const { data: resp } = await api.patch(`/admin/jobs/${job.id}/applicants/${a.id}/status`, { status: next });
            toast.success(`Marked ${a.name || a.email} as ${next}.`);
            // Append the returned status event to the row so the timeline
            // updates without a full refetch.
            if (resp?.event) {
                setData((d) => d && {
                    ...d,
                    applicants: d.applicants.map((x) =>
                        x.id === a.id
                            ? { ...x, status: next, status_events: [...(x.status_events || []), resp.event] }
                            : x
                    ),
                });
            }
        } catch (err) {
            toast.error(formatApiError(err));
            setData((d) => d && { ...d, applicants: d.applicants.map((x) => x.id === a.id ? { ...x, status: prevStatus } : x) });
        } finally {
            setSavingId(null);
        }
    };

    const counts = React.useMemo(() => {
        const c = { any: 0, new: 0, reviewing: 0, shortlisted: 0, rejected: 0, hired: 0 };
        (data?.applicants || []).forEach((a) => {
            c.any += 1;
            if (c[a.status] != null) c[a.status] += 1;
        });
        return c;
    }, [data]);

    const filtered = React.useMemo(() => {
        if (!data) return [];
        if (statusFilter === "any") return data.applicants;
        return data.applicants.filter((a) => a.status === statusFilter);
    }, [data, statusFilter]);

    const openResume = (resumeId) => {
        if (!resumeId) return;
        const token = localStorage.getItem("simonara_admin_token");
        fetch(backendUrl(`/api/admin/resumes/${resumeId}/download`), {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                window.open(url, "_blank", "noopener,noreferrer");
                setTimeout(() => URL.revokeObjectURL(url), 60_000);
            })
            .catch(() => toast.error("Couldn't open the resume."));
    };

    const openApplicationResume = (applicationId) => {
        if (!applicationId) return;
        const token = localStorage.getItem("simonara_admin_token");
        fetch(backendUrl(`/api/admin/applications/${applicationId}/resume`), {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
            .then((blob) => {
                const url = URL.createObjectURL(blob);
                window.open(url, "_blank", "noopener,noreferrer");
                setTimeout(() => URL.revokeObjectURL(url), 60_000);
            })
            .catch(() => toast.error("Couldn't open the applicant resume."));
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex" data-testid="job-applicants-drawer">
            <div className="flex-1 bg-black/40 backdrop-blur-sm" onClick={onClose} />
            <aside className="w-full max-w-[720px] bg-background border-l border-border shadow-xl flex flex-col">
                <header className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                            {job?.req_number || "Applicants"}
                        </div>
                        <div className="font-heading font-semibold text-foreground truncate">
                            {job?.title}
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {loading ? "Loading…" : data ? `${data.count} applicant${data.count === 1 ? "" : "s"}` : ""}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="h-8 w-8 rounded-full hover:bg-muted flex items-center justify-center"
                        data-testid="job-applicants-close"
                        aria-label="Close applicants drawer"
                    >
                        <X size={16} />
                    </button>
                </header>
                {/* Filter chips */}
                {data?.applicants?.length > 0 && (
                    <div className="px-5 py-2 border-b border-border flex flex-wrap items-center gap-1.5" data-testid="applicant-filter-chips">
                        <label className="inline-flex items-center gap-1.5 mr-1 text-[11px] font-medium text-muted-foreground cursor-pointer">
                            <input
                                type="checkbox"
                                checked={filtered.length > 0 && filtered.every((a) => selected.has(a.id))}
                                onChange={() => selectAllFiltered(filtered)}
                                className="accent-emerald-500 cursor-pointer"
                                data-testid="applicant-select-all"
                                aria-label="Select all visible applicants"
                            />
                            Select all
                        </label>
                        {[
                            { k: "any",         label: "All" },
                            { k: "new",         label: "New" },
                            { k: "reviewing",   label: "Reviewing" },
                            { k: "shortlisted", label: "Shortlisted" },
                            { k: "rejected",    label: "Rejected" },
                            { k: "hired",       label: "Hired" },
                        ].map((c) => {
                            const active = statusFilter === c.k;
                            const n = counts[c.k] || 0;
                            return (
                                <button
                                    key={c.k}
                                    onClick={() => setStatusFilter(c.k)}
                                    data-testid={`applicant-filter-${c.k}`}
                                    className={`text-xs px-3 py-1 rounded-full border transition ${
                                        active
                                            ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-800 font-medium"
                                            : "border-border hover:bg-muted/40 text-muted-foreground"
                                    }`}
                                >
                                    {c.label} <span className="tabular-nums opacity-70 ml-1">{n}</span>
                                </button>
                            );
                        })}
                    </div>
                )}
                {/* Bulk actions toolbar — appears when >=1 applicant selected. */}
                {selected.size > 0 && (
                    <div
                        className="px-5 py-2.5 border-b border-border bg-emerald-500/5 flex flex-wrap items-center gap-2"
                        data-testid="applicant-bulk-toolbar"
                    >
                        <span className="text-xs font-semibold text-foreground" data-testid="applicant-bulk-count">
                            {selected.size} selected
                        </span>
                        <button
                            onClick={() => setSelected(new Set())}
                            className="text-[11px] px-2 py-0.5 rounded border border-border text-muted-foreground hover:text-foreground hover:bg-muted/40"
                            data-testid="applicant-bulk-clear"
                        >
                            Clear
                        </button>
                        <div className="flex-1" />
                        <select
                            value={bulkStatus}
                            onChange={(e) => setBulkStatus(e.target.value)}
                            data-testid="applicant-bulk-status"
                            className="text-xs px-2.5 py-1 rounded-md border border-border bg-background"
                        >
                            <option value="new">Move to New</option>
                            <option value="reviewing">Move to Reviewing</option>
                            <option value="shortlisted">Move to Shortlisted</option>
                            <option value="rejected">Move to Rejected</option>
                            <option value="hired">Move to Hired</option>
                        </select>
                        <input
                            type="text"
                            placeholder="Optional note (attached to every event)"
                            value={bulkNote}
                            onChange={(e) => setBulkNote(e.target.value)}
                            className="hidden sm:block text-xs px-2.5 py-1 rounded-md border border-border bg-background w-56"
                            data-testid="applicant-bulk-note"
                        />
                        <button
                            onClick={runBulkUpdate}
                            disabled={bulkSaving}
                            className="btn-green text-xs px-3 py-1 inline-flex items-center gap-1.5 disabled:opacity-60"
                            data-testid="applicant-bulk-apply"
                        >
                            {bulkSaving && <Loader2 size={12} className="animate-spin" />}
                            Update {selected.size}
                        </button>
                    </div>
                )}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                    {loading ? (
                        <div className="py-16 flex justify-center">
                            <Loader2 size={20} className="animate-spin text-emerald-500" />
                        </div>
                    ) : !data || data.applicants.length === 0 ? (
                        <div className="card-soft p-10 text-center" data-testid="job-applicants-empty">
                            <IdCard size={22} className="mx-auto text-muted-foreground" />
                            <div className="mt-2 font-heading font-semibold">No applicants yet.</div>
                            <p className="mt-1 text-sm text-muted-foreground">
                                When candidates apply through the public job page, they'll show up here.
                            </p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="card-soft p-6 text-center text-sm text-muted-foreground" data-testid="job-applicants-no-match">
                            No applicants in <span className="font-medium">{statusFilter}</span>.
                        </div>
                    ) : (
                        filtered.map((a) => {
                            const p = a.passport;
                            const statusCls = STATUS_TONE[a.status] || STATUS_TONE.new;
                            return (
                                <div
                                    key={a.id}
                                    data-testid={`applicant-row-${a.id}`}
                                    className="card-soft p-4 hover:border-emerald-500/40 transition"
                                >
                                    <div className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        checked={selected.has(a.id)}
                                        onChange={() => toggleSelect(a.id)}
                                        className="mt-3 accent-emerald-500 cursor-pointer"
                                        data-testid={`applicant-select-${a.id}`}
                                        aria-label={`Select ${a.name || a.email}`}
                                    />
                                    <Avatar name={a.name} email={a.email} userId={a.user_id} size={44} />
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-baseline gap-2">
                                            <div className="font-heading font-semibold text-foreground truncate">{a.name || a.email}</div>
                                            <div className="relative inline-flex items-center">
                                                <select
                                                    value={a.status}
                                                    disabled={savingId === a.id}
                                                    onChange={(e) => updateStatus(a, e.target.value)}
                                                    data-testid={`applicant-status-select-${a.id}`}
                                                    className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border cursor-pointer appearance-none pr-6 ${statusCls}`}
                                                    aria-label={`Status for ${a.name || a.email}`}
                                                >
                                                    <option value="new">new</option>
                                                    <option value="reviewing">reviewing</option>
                                                    <option value="shortlisted">shortlisted</option>
                                                    <option value="rejected">rejected</option>
                                                    <option value="hired">hired</option>
                                                </select>
                                                {savingId === a.id && (
                                                    <Loader2 size={9} className="absolute right-1.5 top-1/2 -translate-y-1/2 animate-spin opacity-70" />
                                                )}
                                            </div>
                                            {p?.id_verified && (
                                                <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 inline-flex items-center gap-1">
                                                    <ShieldCheck size={9} /> ID verified
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-xs text-muted-foreground truncate">{a.email}</div>
                                        {p ? (
                                            <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                                                {p.desired_position && (
                                                    <span className="inline-flex items-center gap-1"><Sparkles size={10} /> {p.desired_position}</span>
                                                )}
                                                {p.city && (
                                                    <span className="inline-flex items-center gap-1"><MapPin size={10} /> {p.city}</span>
                                                )}
                                                {p.years_experience != null && (<span>· {p.years_experience} yrs</span>)}
                                            </div>
                                        ) : (
                                            <div className="mt-1 text-[11px] text-muted-foreground italic">
                                                No Talent Passport yet — public-form application only.
                                            </div>
                                        )}
                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                            {p && (
                                                <span className={`text-[10px] rounded-full border px-2 py-0.5 font-medium ${completionTone(p.completion_pct)}`}>
                                                    <IdCard size={9} className="inline mr-1 mb-0.5" /> {p.completion_pct}% passport
                                                </span>
                                            )}
                                            {p?.trust_score > 0 && (
                                                <span className="text-[10px] rounded-full border border-sky-500/30 bg-sky-500/10 text-sky-700 px-2 py-0.5 font-medium">
                                                    Verified {p.trust_score}
                                                </span>
                                            )}
                                            {a.source && a.source !== "public_form" && (
                                                <span className="text-[10px] rounded-full border border-slate-500/30 bg-slate-500/10 text-slate-700 px-2 py-0.5 font-medium">
                                                    via {a.source}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                                        {a.application_resume ? (
                                            <button
                                                onClick={() => openApplicationResume(a.id)}
                                                className="text-xs px-3 py-1.5 rounded-full border border-teal-500/30 bg-teal-500/10 text-teal-700 hover:bg-teal-500/20 inline-flex items-center gap-1.5 transition"
                                                data-testid={`applicant-application-resume-${a.id}`}
                                                title={`Applicant-uploaded resume · ${a.application_resume.filename || ""}`}
                                            >
                                                <Paperclip size={11} /> Applied resume
                                            </button>
                                        ) : null}
                                        {p?.resume_id ? (
                                            <button
                                                onClick={() => openResume(p.resume_id)}
                                                className="text-xs px-3 py-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-700 hover:bg-emerald-500/20 inline-flex items-center gap-1.5 transition"
                                                data-testid={`applicant-resume-${a.id}`}
                                                title="View passport resume PDF"
                                            >
                                                <FileText size={11} /> Passport resume
                                            </button>
                                        ) : a.resume_url ? (
                                            <a
                                                href={a.resume_url}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-xs px-3 py-1.5 rounded-full border border-slate-500/30 bg-slate-500/10 text-slate-700 hover:bg-slate-500/20 inline-flex items-center gap-1.5"
                                                data-testid={`applicant-resume-url-${a.id}`}
                                            >
                                                <FileText size={11} /> Uploaded
                                            </a>
                                        ) : null}
                                        {a.email && (
                                            <a
                                                href={`mailto:${a.email}`}
                                                className="text-xs px-3 py-1.5 rounded-full border border-sky-500/30 bg-sky-500/10 text-sky-700 hover:bg-sky-500/20 inline-flex items-center gap-1.5"
                                                data-testid={`applicant-email-${a.id}`}
                                            >
                                                <Mail size={11} /> Email
                                            </a>
                                        )}
                                        {a.phone && (
                                            <a
                                                href={`tel:${a.phone}`}
                                                className="text-xs px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-700 hover:bg-amber-500/20 inline-flex items-center gap-1.5"
                                                data-testid={`applicant-phone-${a.id}`}
                                            >
                                                <Phone size={11} /> Call
                                            </a>
                                        )}
                                        {p?.id && (
                                            <a
                                                href={`/admin/passport-lookup?email=${encodeURIComponent(a.email || "")}`}
                                                target="_blank"
                                                rel="noreferrer"
                                                className="text-xs px-3 py-1.5 rounded-full border border-violet-500/30 bg-violet-500/10 text-violet-700 hover:bg-violet-500/20 inline-flex items-center gap-1.5"
                                                data-testid={`applicant-view-passport-${a.id}`}
                                            >
                                                <IdCard size={11} /> Passport <ExternalLink size={9} />
                                            </a>
                                        )}
                                    </div>
                                </div>
                                {(a.application_resume?.parsed || a.application_resume?.parse_status === "pending") && (
                                    <div className="mt-3 pt-3 border-t border-border" data-testid={`applicant-parsed-${a.id}`}>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                                                AI-parsed
                                            </span>
                                            {a.application_resume?.parse_status === "pending" && (
                                                <span className="text-[10px] px-2 py-0.5 rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 inline-flex items-center gap-1">
                                                    <Loader2 size={9} className="animate-spin" /> parsing…
                                                </span>
                                            )}
                                            {a.application_resume?.parsed?.years != null && (
                                                <span className="text-[11px] px-2 py-0.5 rounded-md border border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-400 font-medium">
                                                    {a.application_resume.parsed.years} yrs experience
                                                </span>
                                            )}
                                        </div>
                                        {a.application_resume?.parsed?.skills?.length > 0 && (
                                            <div className="mt-2 flex flex-wrap gap-1.5">
                                                {a.application_resume.parsed.skills.slice(0, 12).map((sk) => (
                                                    <span key={sk} className="text-[10px] px-2 py-0.5 rounded-md bg-muted/60 border border-border text-foreground">
                                                        {sk}
                                                    </span>
                                                ))}
                                                {a.application_resume.parsed.skills.length > 12 && (
                                                    <span className="text-[10px] px-2 py-0.5 text-muted-foreground">
                                                        +{a.application_resume.parsed.skills.length - 12} more
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {(a.status_events?.length > 0 || a.cover_letter || (a.notes?.length ?? 0) >= 0) && (
                                    <div className="mt-3 pt-3 border-t border-border">
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setExpandedHistoryId((v) => (v === a.id ? null : a.id))
                                            }
                                            className="w-full flex items-center justify-between text-[11px] uppercase tracking-wider font-semibold text-muted-foreground hover:text-foreground transition"
                                            data-testid={`applicant-history-toggle-${a.id}`}
                                        >
                                            <span className="inline-flex items-center gap-1.5">
                                                <History size={11} />
                                                Status history · Notes
                                                {a.status_events?.length > 0 && (
                                                    <span className="text-muted-foreground/70 normal-case tabular-nums">
                                                        · {a.status_events.length} event{a.status_events.length === 1 ? "" : "s"}
                                                    </span>
                                                )}
                                                {a.notes?.length > 0 && (
                                                    <span className="text-muted-foreground/70 normal-case tabular-nums">
                                                        · {a.notes.length} note{a.notes.length === 1 ? "" : "s"}
                                                    </span>
                                                )}
                                            </span>
                                            {expandedHistoryId === a.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                        </button>
                                        {expandedHistoryId === a.id && (
                                            <div className="mt-3 space-y-3" data-testid={`applicant-history-${a.id}`}>
                                                {a.cover_letter && (
                                                    <div className="rounded-md border border-border bg-muted/30 p-3">
                                                        <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">Cover letter</div>
                                                        <p className="text-xs text-foreground whitespace-pre-wrap">{a.cover_letter}</p>
                                                    </div>
                                                )}
                                                {/* Two-way message thread with the applicant. */}
                                                <div data-testid={`applicant-chat-${a.id}`}>
                                                    <div className="mb-1.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground inline-flex items-center gap-1">
                                                        <MessageCircle size={10} /> Applicant chat
                                                    </div>
                                                    <ApplicantChatPanel
                                                        applicationId={a.id}
                                                        role="admin"
                                                        testIdPrefix={`applicant-chat-${a.id}`}
                                                    />
                                                </div>
                                                {/* Private team notes — admin-only. */}
                                                <div className="rounded-md border border-border bg-card p-3" data-testid={`applicant-notes-${a.id}`}>
                                                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-2">
                                                        <StickyNote size={10} /> Private team notes
                                                    </div>
                                                    {a.notes?.length > 0 ? (
                                                        <ul className="space-y-2 mb-3">
                                                            {a.notes.map((n) => (
                                                                <li key={n.id} className="text-xs text-foreground p-2 rounded bg-muted/40 flex items-start gap-2" data-testid={`applicant-note-${a.id}-${n.id}`}>
                                                                    <div className="flex-1 min-w-0">
                                                                        <p className="whitespace-pre-wrap">{n.note}</p>
                                                                        <div className="mt-1 text-[10px] text-muted-foreground">
                                                                            {n.by || "admin"} · {relativeTime(n.created_at)}
                                                                        </div>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => deleteNote(a.id, n.id)}
                                                                        className="text-muted-foreground hover:text-destructive shrink-0"
                                                                        data-testid={`applicant-note-delete-${a.id}-${n.id}`}
                                                                        aria-label="Delete note"
                                                                    >
                                                                        <Trash2 size={11} />
                                                                    </button>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <p className="text-[11px] text-muted-foreground italic mb-2">No notes yet.</p>
                                                    )}
                                                    <div className="flex items-start gap-2">
                                                        <textarea
                                                            rows={2}
                                                            value={noteDrafts[a.id] || ""}
                                                            onChange={(e) => setNoteDrafts((prev) => ({ ...prev, [a.id]: e.target.value }))}
                                                            placeholder="Add a private team note — visible to admins only."
                                                            className="flex-1 text-xs px-2 py-1.5 rounded border border-border bg-background focus:outline-none focus:border-emerald-500 resize-none"
                                                            data-testid={`applicant-note-input-${a.id}`}
                                                        />
                                                        <button
                                                            onClick={() => addNote(a.id)}
                                                            disabled={noteSavingId === a.id || !(noteDrafts[a.id] || "").trim()}
                                                            className="btn-green text-[11px] px-2.5 py-1.5 inline-flex items-center gap-1 disabled:opacity-60"
                                                            data-testid={`applicant-note-add-${a.id}`}
                                                        >
                                                            {noteSavingId === a.id ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />}
                                                            Add
                                                        </button>
                                                    </div>
                                                </div>
                                                {a.status_events?.length > 0 ? (
                                                    <ol className="relative pl-4 space-y-2 border-l border-border">
                                                        {[...a.status_events]
                                                            .sort((x, y) => (x.at > y.at ? -1 : 1))
                                                            .map((evt) => (
                                                                <li key={evt.id || `${evt.at}-${evt.to_status}`} className="relative" data-testid={`applicant-history-event-${a.id}-${evt.id || evt.at}`}>
                                                                    <span className="absolute -left-[9px] top-1.5 h-2 w-2 rounded-full bg-emerald-500 border-2 border-background" />
                                                                    <div className="text-xs text-foreground">
                                                                        {evt.from_status ? (
                                                                            <>Moved from <span className="font-mono uppercase text-[10px] px-1 rounded bg-muted">{evt.from_status}</span> to <span className="font-mono uppercase text-[10px] px-1 rounded bg-emerald-500/10 text-emerald-700">{evt.to_status}</span></>
                                                                        ) : (
                                                                            <>Started at <span className="font-mono uppercase text-[10px] px-1 rounded bg-emerald-500/10 text-emerald-700">{evt.to_status}</span></>
                                                                        )}
                                                                    </div>
                                                                    <div className="text-[10px] text-muted-foreground mt-0.5">
                                                                        by {formatActor(evt)} · {relativeTime(evt.at)}
                                                                    </div>
                                                                    {evt.note && (
                                                                        <p className="text-[11px] text-muted-foreground mt-1 italic">"{evt.note}"</p>
                                                                    )}
                                                                </li>
                                                            ))}
                                                    </ol>
                                                ) : (
                                                    <p className="text-[11px] text-muted-foreground italic">No status changes recorded yet.</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            );
                        })
                    )}
                </div>
            </aside>
        </div>
    );
};

export default JobApplicantsDrawer;
